import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useGoalsDB } from '@/hooks/useGoalsDB';
import { useAuth } from '@/hooks/useAuth';
import { useProfile } from '@/hooks/useProfile';
import { useJourneyProgress } from '@/hooks/useJourneyProgress';
import { useImpersonation } from '@/contexts/ImpersonationContext';
import { useDateFormat } from '@/contexts/DateFormatContext';
import { useDowngradeCheck } from '@/hooks/useDowngradeCheck';
import { useCoachPanelContext } from '@/components/coach/CoachPanelProvider';
import { format } from 'date-fns';
import { isHabitDueToday } from '@/lib/habit-schedule';

import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Target, TrendingUp, Flame, MessageCircle, CalendarCheck, NotebookPen } from 'lucide-react';
import { DashboardHeader } from '@/components/dashboard/DashboardHeader';
import { JourneyNavigation } from '@/components/journey/JourneyNavigation';
import { JourneyWizard } from '@/components/journey/JourneyWizard';
import { TodaysHabits } from '@/components/home/TodaysHabits';
import { WelcomeProModal } from '@/components/subscription/WelcomeProModal';
import { DowngradeModal } from '@/components/subscription/DowngradeModal';

import { FoundingMemberProgressWidget } from '@/components/founding-member/FoundingMemberProgressWidget';
import { CompactWeeklySummary } from '@/components/home/CompactWeeklySummary';
import { CoachedByBadge } from '@/components/home/CoachedByBadge';
import { useInlineHints } from '@/hooks/useInlineHints';
import { ProfileEnrichmentCard } from '@/components/home/ProfileEnrichmentCard';
import { useProfileEnrichment } from '@/hooks/useProfileEnrichment';
import { useReflections } from '@/hooks/useReflections';
import { useWeeklyDigest } from '@/hooks/useWeeklyDigest';
import { WeeklyReflectionDigest } from '@/components/home/WeeklyReflectionDigest';

const WELCOME_MODAL_KEY = 'momentumeq_pro_welcome_seen';

const Home = () => {
  const { goals, loading, getTotalStreak, toggleHabitForToday, refetch } = useGoalsDB();
  const { user } = useAuth();
  const { isImpersonating, impersonatedUser, effectiveUserId } = useImpersonation();
  const {
    needsDowngradeSelection, 
    allGoals: downgradeGoals, 
    allHabits: downgradeHabits,
    loading: downgradeLoading,
    refreshCheck: refreshDowngradeCheck
  } = useDowngradeCheck();
  const { formatDate } = useDateFormat();

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const journey = useJourneyProgress(goals);
  const [activeTab, setActiveTab] = useState('home');
  const [showWizard, setShowWizard] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const { profile, isLoading: profileLoading } = useProfile();
  const coachHints = useInlineHints(goals);
  const enrichment = useProfileEnrichment();
  const reflectionState = useReflections(goals);
  const { todayReflection, reflectionStreak, isActivated: reflectionActivated } = reflectionState;
  const { open: openCoachPanel } = useCoachPanelContext();
  const weeklyDigest = useWeeklyDigest(reflectionState.reflections, goals);

  // Journey-aware next-step CTA
  const goalsPhase = journey.phases.find(p => p.id === 'goals');
  const habitsPhase = journey.phases.find(p => p.id === 'habits');
  const nextStepCTA = useMemo(() => {
    if (!goalsPhase?.isComplete) {
      return {
        title: 'Set Your First Goal',
        description: 'Define what you want to achieve using the 3E framework.',
        actionLabel: 'Go to Goals',
        tab: 'goals',
        Icon: Target,
      };
    }
    if (!habitsPhase?.isComplete) {
      return {
        title: 'Build Daily Habits',
        description: 'Add habits to your goals and start building momentum.',
        actionLabel: 'Go to Habits',
        tab: 'habits',
        Icon: TrendingUp,
      };
    }

    // Returning user: contextual CTA based on current state
    const now = new Date();
    const todayStr = format(now, 'yyyy-MM-dd');
    const todaysHabits = goals.flatMap(g =>
      g.habits.filter(h => isHabitDueToday(h.frequency, h.scheduleDays, h.createdAt, now))
    );
    const habitsCompleted = todaysHabits.filter(h => h.completedDates.includes(todayStr)).length;
    const habitsDueCount = todaysHabits.length;
    const streak = getTotalStreak();

    if (habitsDueCount > 0 && habitsCompleted < habitsDueCount) {
      return {
        title: `${habitsCompleted}/${habitsDueCount} habits done today`,
        description: 'Keep the momentum going — check off your remaining habits.',
        actionLabel: 'Continue',
        tab: 'habits',
        Icon: CalendarCheck,
      };
    }
    if (habitsDueCount > 0 && habitsCompleted === habitsDueCount && !todayReflection && reflectionActivated) {
      return {
        title: 'Take 60 seconds to reflect',
        description: 'All habits done — capture what energized and drained you today.',
        actionLabel: 'Reflect',
        tab: 'reflect',
        Icon: NotebookPen,
      };
    }
    if (todayReflection && reflectionStreak >= 3) {
      return {
        title: `Reflection streak: ${reflectionStreak} days — nice consistency`,
        description: 'Daily reflection builds self-awareness and drives better decisions.',
        actionLabel: 'View Reflection',
        tab: 'reflect',
        Icon: NotebookPen,
      };
    }
    if (habitsDueCount > 0 && habitsCompleted === habitsDueCount && streak >= 3) {
      return {
        title: `${streak}-day streak — keep it alive!`,
        description: 'All habits done today! Your consistency is paying off.',
        actionLabel: 'View Progress',
        tab: 'habits',
        Icon: Flame,
      };
    }
    if (goals.length > 0) {
      return {
        title: 'Review your goal progress',
        description: 'Check in on your goals and plan your next steps.',
        actionLabel: 'Review Goals',
        tab: 'goals',
        Icon: TrendingUp,
      };
    }
    return {
      title: 'Ask your AI coach for guidance',
      description: 'Get personalized suggestions for your next move.',
      actionLabel: 'Open Coach',
      tab: 'coach_panel',
      Icon: MessageCircle,
    };
  }, [goalsPhase?.isComplete, habitsPhase?.isComplete, goals, getTotalStreak, todayReflection, reflectionStreak, reflectionActivated]);

  // Debounced journey refetch to avoid stale re-renders during rapid habit toggling
  const journeyRefetchTimer = useRef<ReturnType<typeof setTimeout>>();
  const debouncedJourneyRefetch = useCallback(() => {
    clearTimeout(journeyRefetchTimer.current);
    journeyRefetchTimer.current = setTimeout(() => {
      journey.refetch();
    }, 500);
  }, [journey]);

  // Derive first name from profile or impersonated user, falling back to email prefix
  const firstName = useMemo(() => {
    const extractName = (first: string | null | undefined, display: string | null | undefined, email: string | null | undefined) => {
      if (first) return first;
      if (display && !display.includes('@')) return display.split(' ')[0];
      return email?.split('@')[0] || '';
    };
    if (isImpersonating && impersonatedUser) {
      return extractName(impersonatedUser.first_name, impersonatedUser.display_name, undefined);
    }
    return extractName(profile?.first_name, profile?.display_name, user?.email);
  }, [profile, isImpersonating, impersonatedUser, user]);

  // Calculate greeting based on time of day
  const greeting = useMemo(() => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  }, []);

  // Format current date
  const formattedDate = useMemo(() => {
    return formatDate(new Date(), 'weekday');
  }, [formatDate]);

  // Check if values phase is complete (first phase with >= 3 core values)
  const valuesPhaseComplete = journey.phases.find(p => p.id === 'values')?.isComplete ?? false;

  // Redirect to main dashboard (values) if user hasn't completed values phase
  useEffect(() => {
    if (!journey.isLoading && !valuesPhaseComplete) {
      navigate('/dashboard', { replace: true });
    }
  }, [journey.isLoading, valuesPhaseComplete, navigate]);

  // Handle upgrade success redirect
  useEffect(() => {
    const upgraded = searchParams.get('upgraded');
    if (upgraded === 'true') {
      // Check if user has already seen the welcome modal
      const hasSeenWelcome = localStorage.getItem(WELCOME_MODAL_KEY);
      if (!hasSeenWelcome) {
        setShowWelcomeModal(true);
      }
      // Clean up URL without causing navigation
      window.history.replaceState({}, '', '/home');
    }
  }, [searchParams]);

  const handleWelcomeModalClose = () => {
    setShowWelcomeModal(false);
    localStorage.setItem(WELCOME_MODAL_KEY, 'true');
  };

  const handleTabChange = (tab: string) => {
    if (tab === 'home') {
      setActiveTab('home');
    } else {
      // Navigate to main dashboard with the selected tab
      navigate('/dashboard', { state: { activeTab: tab } });
    }
  };

  // Navigate to Plan tab with a specific milestone expanded
  const handleNavigateToPlan = (milestoneIndex: number) => {
    navigate('/dashboard', { 
      state: { 
        activeTab: 'plan',
        expandedMilestoneIndex: milestoneIndex 
      } 
    });
  };

  const handleWizardClose = () => {
    setShowWizard(false);
  };

  const handleDowngradeComplete = () => {
    refreshDowngradeCheck();
    refetch();
  };

  // Don't render if redirecting (values not complete)
  if (!journey.isLoading && !valuesPhaseComplete) {
    return null;
  }

  const isDataLoading = loading || journey.isLoading;
  const totalStreak = isDataLoading ? 0 : getTotalStreak();

  return (
    <div className="min-h-screen bg-background">
      <DashboardHeader onOpenWizard={() => setShowWizard(true)} valuesComplete={!journey.isLoading && valuesPhaseComplete} />

      {/* Hero Greeting Section with Gradient */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-background to-background" />
        <div className="absolute inset-0 bg-gradient-to-r from-primary/10 via-transparent to-accent/10" />
        <div className="relative container max-w-[1200px] mx-auto px-6">
          <div className="pt-[60px] pb-[40px]">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-3">
              {profileLoading ? (
                <Skeleton className="h-10 w-64 sm:w-80 inline-block" />
              ) : (
                <>{greeting}{firstName ? `, ${firstName}` : ''}!</>
              )}
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground">
              {formattedDate}
              {!isDataLoading && totalStreak > 0 && (
                <span className="ml-2">
                  · <span className="text-primary font-medium">{totalStreak}-day streak</span> 🔥
                </span>
              )}
            </p>
            <div className="mt-2">
              <CoachedByBadge />
            </div>
          </div>
        </div>
      </div>

      {isDataLoading ? (
        <main id="main-content" className="container max-w-[1200px] mx-auto px-6 py-8 space-y-8">
          {/* Skeleton: navigation tabs */}
          <div className="flex gap-2">
            {[1, 2, 3, 4, 5].map(i => (
              <Skeleton key={i} className="h-9 w-20 rounded-md" />
            ))}
          </div>
          {/* Skeleton: habit cards */}
          <div className="space-y-3">
            <Skeleton className="h-5 w-40" />
            {[1, 2, 3].map(i => (
              <Skeleton key={i} className="h-16 w-full rounded-lg" />
            ))}
          </div>
          {/* Skeleton: weekly summary */}
          <Skeleton className="h-32 w-full rounded-lg" />
        </main>
      ) : (
      <main id="main-content" className="container max-w-[1200px] mx-auto px-6 py-8 pb-24 space-y-8">
        {/* Unified Journey Navigation with Home tab */}
        <JourneyNavigation
          phases={journey.phases}
          overallProgress={journey.overallProgress}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          showHomeTab={true}
        />

        {/* Journey guidance CTA */}
        <Card className="card-elevated border-primary/20 bg-primary/5">
          <CardContent className="flex items-center gap-4 py-5">
            <div className="p-3 rounded-lg bg-primary/10">
              <nextStepCTA.Icon className="w-6 h-6 text-primary" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold">{nextStepCTA.title}</h3>
              <p className="text-sm text-muted-foreground">{nextStepCTA.description}</p>
            </div>
            <Button onClick={() => {
              if (nextStepCTA.tab === 'coach_panel') {
                openCoachPanel();
              } else if (nextStepCTA.tab === 'reflect') {
                document.getElementById('reflection-prompt')?.scrollIntoView({ behavior: 'smooth' });
              } else {
                navigate('/dashboard', { state: { activeTab: nextStepCTA.tab } });
              }
            }}>
              {nextStepCTA.actionLabel}
            </Button>
          </CardContent>
        </Card>

        {/* Weekly Reflection Digest */}
        {weeklyDigest.digest && !weeklyDigest.dismissed && (
          <WeeklyReflectionDigest
            digest={weeklyDigest.digest}
            onDismiss={weeklyDigest.dismiss}
          />
        )}

        {/* Today's Habits Section - Primary Focus */}
        <TodaysHabits
          goals={goals}
          onToggleHabit={async (goalId, habitId) => {
            await toggleHabitForToday(goalId, habitId);
            debouncedJourneyRefetch();
          }}
          onViewAllHabits={() => handleTabChange('habits')}
          onNavigateToPlan={handleNavigateToPlan}
          coachHints={coachHints}
          reflection={reflectionState}
        />

        {/* Compact weekly summary + goal momentum */}
        <CompactWeeklySummary
          goals={goals}
          reflections={reflectionState.reflections}
          onNavigateToGoals={() => handleTabChange('goals')}
          onNavigateToHabits={() => handleTabChange('habits')}
        />

        {/* Profile Enrichment Prompt */}
        {enrichment.shouldShow && (
          <ProfileEnrichmentCard
            onDismiss={enrichment.dismiss}
            onDismissPermanently={enrichment.dismissPermanently}
          />
        )}

        {/* Founding Member Progress */}
        <FoundingMemberProgressWidget />

        {/* Journey Wizard Modal */}
        <JourneyWizard
          phases={journey.phases}
          isOpen={showWizard}
          onClose={handleWizardClose}
          onNavigate={(tab) => {
            handleTabChange(tab);
            handleWizardClose();
          }}
        />

        {/* Welcome to Pro Modal */}
        <WelcomeProModal
          open={showWelcomeModal}
          onClose={handleWelcomeModalClose}
        />

        {/* Downgrade Selection Modal */}
        <DowngradeModal
          isOpen={needsDowngradeSelection}
          allGoals={downgradeGoals}
          allHabits={downgradeHabits}
          onComplete={handleDowngradeComplete}
          onUpgrade={() => navigate('/pricing')}
        />


      </main>
      )}
    </div>
  );
};

export default Home;
